import { START_LOCATION } from 'vue-router';
import { authGuard } from '@auth0/auth0-vue';

import { i18n } from '@/i18n';
import { auth, authCallbackError } from '@/plugins/auth';

export const setTitle = (route) => {
    document.title = i18n
        ? i18n.t(route.meta.title || 'app.title')
        : '';
};

/**
 * Builds the /auth-error route string with error query params.
 */
export const getAuthErrorRoute = (authError) => {
    const { error, error_description } = authError;
    const query = new URLSearchParams();

    if (error) query.set('error', error);
    if (error_description) query.set('error_description', error_description);

    const queryString = query.toString();

    return `/auth-error${queryString ? `?${queryString}` : ''}`;
};

/**
 * Navigation guard that runs before each route change. Uses Vue Router 4's
 * return-style guard signature (truthy = continue, false = cancel,
 * RouteLocation = redirect) instead of the imperative `next()` callback,
 * which avoids a "next was never called" warning when authGuard triggers
 * loginWithRedirect and halts navigation by returning false.
 *
 * Auth handling is delegated to the SDK's authGuard (which awaits isLoading,
 * triggers loginWithRedirect on missing auth, and propagates appState.target).
 */
export const beforeEachRoute = async (to, from) => {
    const isInitialLoad = !from || from === START_LOCATION;

    if (!isInitialLoad) {
        window.performance.clearMarks(`transition_${to.name}_start`);
        window.performance.mark(`transition_${to.name}_start`);
        window.performance.mark('transition_start');
    }

    if (to.meta?.isPublic) {
        // The SDK's errorPath does router.push('/auth-error') without query
        // params (it strips ?error=&error_description=). Restore them from the
        // boot-captured callback error so the URL stays useful for debugging.
        if (to.name === 'authError' && authCallbackError) {
            const hasErrorQuery = to.query?.error || to.query?.error_description;
            if (!hasErrorQuery) {
                return getAuthErrorRoute(authCallbackError);
            }
        }
        return true;
    }

    // Circuit breaker: when Auth0 returns a login/redirect-callback error
    // (e.g. locked account, missing permissions), authCallbackError is set at
    // boot from ?error=&state= and the user remains unauthenticated. Without
    // this early exit, authGuard would trigger loginWithRedirect, Auth0 would
    // return the same error, and the cycle would repeat indefinitely.
    // Redirecting to /auth-error (a public route) breaks the loop.
    // Intentionally NOT using auth.error — the SDK sets that ref for any failed
    // proxied call (including routine token refresh), which is not a terminal
    // login failure.
    if (authCallbackError) {
        // consent_required means Auth0 can't silently grant consent (common on
        // localhost where the app is treated as third-party). Re-enter the login
        // flow with prompt=consent so Auth0 shows the accept/deny screen.
        if (authCallbackError.error === 'consent_required') {
            auth.loginWithRedirect({
                authorizationParams: { prompt: 'consent' },
                appState: { target: to.fullPath },
            });
            return false;
        }

        return getAuthErrorRoute(authCallbackError);
    }

    return authGuard(to);
};

/**
 * Navigation guard that runs before route resolves.
 * Sets the document title.
 */
export const beforeResolveRoute = (to) => {
    setTitle(to);
    return true;
};

/**
 * Hook that runs after navigation completes.
 * Updates page title.
 */
export const onAfterEach = (to) => {
    setTitle(to);
};
